import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useTeamCompetitionData from './useTeamCompetitionData';
import NavLink from './common/NavLink';
import { slugify } from '../utils/slugify';
import { useAuth } from '../contexts/AuthContext';
import {
  analyzeConditionScenario,
  createConditionScenario,
  deleteConditionScenario,
  fetchConditionScenarios,
  updateConditionScenario,
} from '../services/api';

const conditionTypes = [
  { value: 'TEAM_POSITION_RANGE', label: 'Drużyna na miejscach od-do' },
  { value: 'TEAM_POINTS', label: 'Punkty drużyny' },
  { value: 'POSITION_POINTS', label: 'Punkty miejsca w tabeli' },
  { value: 'TEAM_RELATIVE_POSITION', label: 'Drużyna nad/pod drużyną' },
  { value: 'TEAM_POINTS_DIFF', label: 'Różnica punktów drużyn' },
  { value: 'POSITION_POINTS_DIFF', label: 'Różnica punktów miejsc' },
];

const initialConditionTypes = [
  { value: 'specific_match', label: 'Wynik meczu' },
  { value: 'all_wins', label: 'Same zwycięstwa' },
  { value: 'all_losses', label: 'Same porażki' },
  { value: 'elo_change', label: 'Zmiana ELO drużyny' },
  { value: 'points_change', label: 'Dodaj/odejmij punkty drużynie' },
];

const defaultLeaf = (teams) => ({
  type: 'TEAM_POSITION_RANGE',
  teamId: teams?.[0]?.teamId || teams?.[0]?.id || null,
  otherTeamId: teams?.[1]?.teamId || teams?.[1]?.id || null,
  minPosition: 1,
  maxPosition: 4,
  position: 1,
  otherPosition: 2,
  comparison: '>=',
  points: 40,
  diff: 3,
});

const defaultDefinition = (teams) => ({
  operator: 'AND',
  children: [defaultLeaf(teams)],
});

const teamIdOf = (team) => team.teamId || team.id;
const teamNameOf = (team) => team.teamName || team.name;

const formatMatchDate = (timestamp) => {
  if (!timestamp) return '-';
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
};

const numberValue = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  return Number(value);
};

const updateAtPath = (node, path, updater) => {
  if (path.length === 0) return updater(node);
  const [index, ...rest] = path;
  return {
    ...node,
    children: node.children.map((child, childIndex) =>
      childIndex === index ? updateAtPath(child, rest, updater) : child
    ),
  };
};

const removeAtPath = (node, path) => {
  if (path.length === 1) {
    return {
      ...node,
      children: node.children.filter((_, index) => index !== path[0]),
    };
  }
  const [index, ...rest] = path;
  return {
    ...node,
    children: node.children.map((child, childIndex) =>
      childIndex === index ? removeAtPath(child, rest) : child
    ),
  };
};

const collectScenarioTeamIds = (node, result = new Set()) => {
  if (!node) return result;
  if (node.teamId) result.add(Number(node.teamId));
  if (node.otherTeamId) result.add(Number(node.otherTeamId));
  if (node.children) {
    node.children.forEach((child) => collectScenarioTeamIds(child, result));
  }
  return result;
};

function ConditionEditor({ node, path, teams, onChange, onRemove, root = false }) {
  const isGroup = node.children && node.children.length > 0;

  if (isGroup) {
    return (
      <Paper sx={{ p: 1.5, backgroundColor: '#202020', border: '1px solid #333', borderRadius: 1 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                label="Operator"
                value={node.operator || 'AND'}
                onChange={(event) => onChange(path, { ...node, operator: event.target.value })}
              >
                <MenuItem value="AND">ORAZ</MenuItem>
                <MenuItem value="OR">LUB</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Dodaj warunek">
              <IconButton
                color="primary"
                onClick={() => onChange(path, { ...node, children: [...node.children, defaultLeaf(teams)] })}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onChange(path, { ...node, children: [...node.children, defaultDefinition(teams)] })}
            >
              Dodaj grupę
            </Button>
            {!root && (
              <Tooltip title="Usuń grupę">
                <IconButton color="error" onClick={() => onRemove(path)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {node.children.map((child, index) => (
            <ConditionEditor
              key={index}
              node={child}
              path={[...path, index]}
              teams={teams}
              onChange={onChange}
              onRemove={onRemove}
            />
          ))}
        </Stack>
      </Paper>
    );
  }

  const setField = (field, value) => onChange(path, { ...node, [field]: value });

  return (
    <Paper sx={{ p: 1.5, backgroundColor: '#181818', border: '1px solid #333', borderRadius: 1 }}>
      <Stack spacing={1.2}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 280px', maxWidth: { md: 360 } }}>
          <FormControl fullWidth size="small">
            <InputLabel>Warunek</InputLabel>
            <Select label="Warunek" value={node.type} onChange={(event) => setField('type', event.target.value)}>
              {conditionTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          </Box>
          <Tooltip title="Usuń warunek">
            <IconButton color="error" onClick={() => onRemove(path)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.2,
            alignItems: 'center',
          }}
        >

        {['TEAM_POSITION_RANGE', 'TEAM_POINTS', 'TEAM_RELATIVE_POSITION', 'TEAM_POINTS_DIFF'].includes(node.type) && (
          <Box>
            <TeamSelect teams={teams} value={node.teamId} onChange={(value) => setField('teamId', value)} label="Drużyna" />
          </Box>
        )}

        {['TEAM_RELATIVE_POSITION', 'TEAM_POINTS_DIFF'].includes(node.type) && (
          <Box>
            <TeamSelect teams={teams} value={node.otherTeamId} onChange={(value) => setField('otherTeamId', value)} label="Rywal" />
          </Box>
        )}

        {node.type === 'TEAM_POSITION_RANGE' && (
          <>
            <TextField fullWidth size="small" label="Od" type="number" value={node.minPosition || ''} onChange={(e) => setField('minPosition', numberValue(e.target.value))} />
            <TextField fullWidth size="small" label="Do" type="number" value={node.maxPosition || ''} onChange={(e) => setField('maxPosition', numberValue(e.target.value))} />
          </>
        )}

        {['TEAM_POINTS', 'POSITION_POINTS', 'TEAM_POINTS_DIFF', 'POSITION_POINTS_DIFF'].includes(node.type) && (
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Min/max</InputLabel>
              <Select label="Min/max" value={node.comparison || '>='} onChange={(e) => setField('comparison', e.target.value)}>
                <MenuItem value=">=">min.</MenuItem>
                <MenuItem value="<=">max.</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {node.type === 'TEAM_RELATIVE_POSITION' && (
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Relacja</InputLabel>
              <Select label="Relacja" value={node.comparison || 'ABOVE'} onChange={(e) => setField('comparison', e.target.value)}>
                <MenuItem value="ABOVE">nad</MenuItem>
                <MenuItem value="BELOW">pod</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {['POSITION_POINTS', 'POSITION_POINTS_DIFF'].includes(node.type) && (
          <TextField fullWidth size="small" label="Miejsce" type="number" value={node.position || ''} onChange={(e) => setField('position', numberValue(e.target.value))} />
        )}

        {node.type === 'POSITION_POINTS_DIFF' && (
          <TextField fullWidth size="small" label="Drugie" type="number" value={node.otherPosition || ''} onChange={(e) => setField('otherPosition', numberValue(e.target.value))} />
        )}

        {['TEAM_POINTS', 'POSITION_POINTS'].includes(node.type) && (
          <TextField fullWidth size="small" label="Pkt" type="number" value={node.points || ''} onChange={(e) => setField('points', numberValue(e.target.value))} />
        )}

        {['TEAM_POINTS_DIFF', 'POSITION_POINTS_DIFF'].includes(node.type) && (
          <TextField fullWidth size="small" label="Różnica" type="number" value={node.diff || ''} onChange={(e) => setField('diff', numberValue(e.target.value))} />
        )}
        </Box>
      </Stack>
    </Paper>
  );
}

function TeamSelect({ teams, value, onChange, label }) {
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value || ''} onChange={(event) => onChange(Number(event.target.value))}>
        {teams.map((team) => (
          <MenuItem key={teamIdOf(team)} value={teamIdOf(team)}>{teamNameOf(team)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function InitialConditionsForm({ register, watch, setValue, fields, append, remove, matches, teams }) {
  const watchedConditions = watch('initialConditions');

  const handleTeamChange = (index, teamId) => {
    const team = teams.find((item) => Number(teamIdOf(item)) === Number(teamId));
    const defaultElo = team?.elo || team?.eloRanking || team?.elo_ranking || 1400;
    setValue(`initialConditions.${index}.newElo`, defaultElo, { shouldValidate: true });
  };

  return (
    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', backgroundColor: '#1e1e1e', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ px: 1 }}>Warunek</TableCell>
            <TableCell align="center" sx={{ px: 1 }}>Mecz / Drużyna</TableCell>
            <TableCell align="center" sx={{ px: 1 }}>Gole gospodarza</TableCell>
            <TableCell align="center" sx={{ px: 1 }}>Gole gościa</TableCell>
            <TableCell align="center" sx={{ px: 1 }}>Nowe ELO</TableCell>
            <TableCell align="center" sx={{ px: 1 }}>Akcje</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, index) => {
            const conditionType = watchedConditions?.[index]?.type || 'specific_match';
            return (
              <TableRow key={field.id}>
                <TableCell align="center" sx={{ px: 1, py: 1 }}>
                  <FormControl sx={{ minWidth: 170 }} size="small">
                    <InputLabel id={`initial-condition-type-label-${index}`}>Warunek</InputLabel>
                    <Select
                      labelId={`initial-condition-type-label-${index}`}
                      label="Warunek"
                      defaultValue={field.type || 'specific_match'}
                      {...register(`initialConditions.${index}.type`)}
                    >
                      {initialConditionTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>

                <TableCell align="center" sx={{ px: 1, py: 1 }}>
                  {conditionType === 'specific_match' ? (
                    <Autocomplete
                      options={matches || []}
                      size="small"
                      sx={{ minWidth: 280 }}
                      value={(matches || []).find((match) => match.matchId === Number(watch(`initialConditions.${index}.matchId`))) || null}
                      getOptionLabel={(option) =>
                        `[${option.round || '-'}] ${option.homeTeamName} - ${option.awayTeamName} ${
                          option.status === 'ended'
                            ? `(${option.homeGoals}:${option.awayGoals})`
                            : '?:?'
                        }`
                      }
                      filterOptions={(options, state) => {
                        const search = state.inputValue.toLowerCase();
                        return options.filter((option) =>
                          (option.homeTeamName || '').toLowerCase().includes(search) ||
                          (option.awayTeamName || '').toLowerCase().includes(search) ||
                          String(option.round || '').toLowerCase().includes(search)
                        );
                      }}
                      onChange={(_, newValue) => {
                        setValue(`initialConditions.${index}.matchId`, newValue ? newValue.matchId : '');
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Mecz" variant="outlined" required />
                      )}
                    />
                  ) : conditionType === 'points_change' ? (
                    <Box sx={{ display: 'flex', gap: 1, minWidth: 280 }}>
                      <FormControl sx={{ flexGrow: 2 }} size="small">
                        <InputLabel id={`initial-team-select-label-${index}`}>Drużyna</InputLabel>
                        <Select
                          labelId={`initial-team-select-label-${index}`}
                          label="Drużyna"
                          defaultValue=""
                          {...register(`initialConditions.${index}.teamId`)}
                          required
                        >
                          <MenuItem value="">Wybierz drużynę</MenuItem>
                          {teams.map((team) => (
                            <MenuItem key={teamIdOf(team)} value={teamIdOf(team)}>{teamNameOf(team)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Punkty"
                        type="number"
                        size="small"
                        sx={{ width: 100 }}
                        {...register(`initialConditions.${index}.pointsDelta`, { valueAsNumber: true })}
                        required
                      />
                    </Box>
                  ) : (
                    <FormControl sx={{ minWidth: 190 }} size="small">
                      <InputLabel id={`initial-team-select-label-${index}`}>Drużyna</InputLabel>
                      <Select
                        labelId={`initial-team-select-label-${index}`}
                        label="Drużyna"
                        defaultValue=""
                        {...register(`initialConditions.${index}.teamId`, {
                          onChange: (event) => handleTeamChange(index, event.target.value),
                        })}
                      >
                        <MenuItem value="">Wybierz drużynę</MenuItem>
                        {teams.map((team) => (
                          <MenuItem key={teamIdOf(team)} value={teamIdOf(team)}>{teamNameOf(team)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </TableCell>

                <TableCell align="center" sx={{ px: 1, py: 1 }}>
                  {conditionType === 'specific_match' ? (
                    <TextField
                      label="Gole gospodarza"
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 90 }}
                      InputLabelProps={{ shrink: true }}
                      {...register(`initialConditions.${index}.homeGoals`, { valueAsNumber: true, min: 0 })}
                    />
                  ) : '-'}
                </TableCell>

                <TableCell align="center" sx={{ px: 1, py: 1 }}>
                  {conditionType === 'specific_match' ? (
                    <TextField
                      label="Gole gościa"
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 90 }}
                      InputLabelProps={{ shrink: true }}
                      {...register(`initialConditions.${index}.awayGoals`, { valueAsNumber: true, min: 0 })}
                    />
                  ) : '-'}
                </TableCell>

                <TableCell align="center" sx={{ px: 1, py: 1 }}>
                  {conditionType === 'elo_change' ? (
                    <TextField
                      label="Nowe ELO"
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 100 }}
                      InputLabelProps={{ shrink: true }}
                      {...register(`initialConditions.${index}.newElo`, { valueAsNumber: true, min: 0 })}
                    />
                  ) : '-'}
                </TableCell>

                <TableCell align="center" sx={{ px: 1, py: 1 }}>
                  <IconButton
                    onClick={() => remove(index)}
                    disabled={fields.length <= 1}
                    sx={{ color: 'error.main', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
        <Button variant="outlined" onClick={() => append({ type: 'specific_match' })}>
          Dodaj kolejny warunek
        </Button>
      </Box>
    </TableContainer>
  );
}

function ExampleSeason({ example }) {
  if (!example?.found) {
    return <Alert severity="info">Nie znaleziono sezonu spełniającego scenariusz w tej próbce.</Alert>;
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Tabela końcowa</Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ px: 1.5 }}>#</TableCell>
                <TableCell sx={{ px: 1.5 }}>Drużyna</TableCell>
                <TableCell align="right" sx={{ px: 1.5 }}>Pkt</TableCell>
                <TableCell align="right" sx={{ px: 1.5 }}>Bramki</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {example.finalTable.map((row) => (
                <TableRow key={row.teamId}>
                  <TableCell sx={{ px: 1.5 }}>{row.position}</TableCell>
                  <TableCell sx={{ px: 1.5 }}>
                    <TeamLink teamId={row.teamId} teamName={row.teamName} />
                  </TableCell>
                  <TableCell align="right" sx={{ px: 1.5 }}>{row.points}</TableCell>
                  <TableCell align="right" sx={{ px: 1.5 }}>{row.goalsScored}:{row.goalsConceded}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} md={7}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Wyniki meczów</Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e', maxHeight: 540 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ px: 1.5 }}>Kolejka</TableCell>
                <TableCell sx={{ px: 1.5 }}>Data</TableCell>
                <TableCell sx={{ px: 1.5 }}>Gospodarz</TableCell>
                <TableCell align="center" sx={{ px: 1.5 }}>Wynik</TableCell>
                <TableCell sx={{ px: 1.5 }}>Gość</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {example.matches.map((match) => (
                <TableRow key={match.matchId}>
                  <TableCell align="center" sx={{ px: 1.5 }}>{match.round || '-'}</TableCell>
                  <TableCell sx={{ px: 1.5 }}>{formatMatchDate(match.matchDate)}</TableCell>
                  <TableCell sx={{ px: 1.5 }}>
                    <TeamLink teamId={match.homeTeamId} teamName={match.homeTeamName} />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 1.5 }}>{match.homeGoals}:{match.awayGoals}</TableCell>
                  <TableCell sx={{ px: 1.5 }}>
                    <TeamLink teamId={match.awayTeamId} teamName={match.awayTeamName} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
}

function TeamLink({ teamId, teamName }) {
  return (
    <Typography
      component={NavLink}
      to={`/team/${teamId}/${slugify(teamName)}`}
      sx={{
        color: 'inherit',
        font: 'inherit',
        fontWeight: 'inherit',
        textDecoration: 'none',
        '&:hover': { color: 'inherit', textDecoration: 'none' },
      }}
    >
      {teamName}
    </Typography>
  );
}

function KeyMatchesTable({ rows }) {
  const [roundFilter, setRoundFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('influence');
  const [sortDirection, setSortDirection] = useState('desc');

  const rounds = useMemo(() => {
    return [...new Set((rows || []).map((row) => row.round).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), 'pl', { numeric: true }));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const filtered = (rows || []).filter((row) => roundFilter === 'ALL' || row.round === roundFilter);
    return [...filtered].sort((a, b) => {
      let result = 0;
      if (sortBy === 'round') {
        result = String(a.round || '').localeCompare(String(b.round || ''), 'pl', { numeric: true });
      } else if (sortBy === 'date') {
        result = (a.matchDate || Number.MAX_SAFE_INTEGER) - (b.matchDate || Number.MAX_SAFE_INTEGER);
      } else if (sortBy === 'conditionalProbability') {
        result = a.conditionalProbabilityPercent - b.conditionalProbabilityPercent;
      } else {
        result = Math.abs(a.influencePercentPoints) - Math.abs(b.influencePercentPoints);
      }
      return sortDirection === 'asc' ? result : -result;
    });
  }, [rows, roundFilter, sortBy, sortDirection]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortBy(column);
    setSortDirection(column === 'round' || column === 'date' ? 'asc' : 'desc');
  };

  const sortLabel = (column, label) => (
    <TableSortLabel
      active={sortBy === column}
      direction={sortBy === column ? sortDirection : 'asc'}
      onClick={() => handleSort(column)}
      sx={{ color: 'inherit', '&.Mui-active': { color: 'inherit' }, '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
    >
      {label}
    </TableSortLabel>
  );

  if (!rows?.length) {
    return <Alert severity="info">Brak wyników meczów w tej sekcji.</Alert>;
  }
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Kolejka</InputLabel>
          <Select label="Kolejka" value={roundFilter} onChange={(event) => setRoundFilter(event.target.value)}>
            <MenuItem value="ALL">Wszystkie</MenuItem>
            {rounds.map((round) => (
              <MenuItem key={round} value={round}>{round}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!visibleRows.length ? (
        <Alert severity="info">Brak meczów po wybraniu filtrów.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ px: 1.5 }}>{sortLabel('date', 'Data')}</TableCell>
                <TableCell align="center" sx={{ px: 1.5 }}>{sortLabel('round', 'Kolejka')}</TableCell>
                <TableCell sx={{ px: 1.5 }}>Mecz</TableCell>
                <TableCell align="center" sx={{ px: 1.5 }}>Wynik</TableCell>
                <TableCell align="right" sx={{ px: 1.5 }}>{sortLabel('conditionalProbability', 'Przy tym wyniku')}</TableCell>
                <TableCell align="right" sx={{ px: 1.5 }}>{sortLabel('influence', 'Zmiana')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={`${row.matchId}-${row.outcome}`}>
                  <TableCell sx={{ px: 1.5 }}>{formatMatchDate(row.matchDate)}</TableCell>
                  <TableCell align="center" sx={{ px: 1.5 }}>{row.round || '-'}</TableCell>
                  <TableCell sx={{ px: 1.5 }}>
                    <TeamLink teamId={row.homeTeamId} teamName={row.homeTeamName} />
                    <Box component="span" sx={{ mx: 0.8, color: '#888' }}>-</Box>
                    <TeamLink teamId={row.awayTeamId} teamName={row.awayTeamName} />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 1.5 }}>{row.outcome}</TableCell>
                  <TableCell align="right" sx={{ px: 1.5 }}>{row.conditionalProbabilityPercent}%</TableCell>
                  <TableCell align="right" sx={{ px: 1.5, color: row.influencePercentPoints > 0 ? '#66bb6a' : '#ef5350', fontWeight: 700 }}>
                    {row.influencePercentPoints > 0 ? '+' : ''}{row.influencePercentPoints} pp
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

export default function ScenarioLab() {
  const { competitionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { teams, matches, name, group, loading: leagueLoading, error: leagueError } = useTeamCompetitionData(competitionId);
  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [scenarioName, setScenarioName] = useState('Nowy scenariusz');
  const [definition, setDefinition] = useState(null);
  const [useInitialConditions, setUseInitialConditions] = useState(false);
  const [hideScenarioTeamMatches, setHideScenarioTeamMatches] = useState(true);
  const [result, setResult] = useState(null);
  const [resultTab, setResultTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const { register, control, watch, setValue, getValues } = useForm({
    defaultValues: { initialConditions: [{ type: 'specific_match' }] },
  });
  const { fields: initialConditionFields, append: appendInitialCondition, remove: removeInitialCondition } = useFieldArray({
    control,
    name: 'initialConditions',
  });

  useEffect(() => {
    if (!definition && teams.length) {
      setDefinition(defaultDefinition(teams));
    }
  }, [teams, definition]);

  const loadScenarios = useCallback(async () => {
    if (!user || !competitionId) return;
    const data = await fetchConditionScenarios(competitionId);
    setScenarios(data);
  }, [user, competitionId]);

  useEffect(() => {
    if (!authLoading && user) {
      loadScenarios().catch((err) => setError(err.response?.data?.error || err.message));
    }
  }, [authLoading, user, loadScenarios]);

  const leagueTitle = useMemo(() => {
    const cleanGroup = group && group.trim() ? group.trim() : '';
    const suffix = cleanGroup ? ` (${cleanGroup})` : '';
    return `${name || 'Liga'}${suffix}`;
  }, [name, group]);

  const scenarioTeamIds = useMemo(() => collectScenarioTeamIds(definition), [definition]);

  const filterKeyMatches = useCallback((rows) => {
    if (!hideScenarioTeamMatches) return rows || [];
    return (rows || []).filter((row) =>
      !scenarioTeamIds.has(Number(row.homeTeamId)) && !scenarioTeamIds.has(Number(row.awayTeamId))
    );
  }, [hideScenarioTeamMatches, scenarioTeamIds]);

  const handleDefinitionChange = (path, nextNode) => {
    setDefinition((current) => updateAtPath(current, path, () => nextNode));
  };

  const handleRemove = (path) => {
    if (path.length === 0) return;
    setDefinition((current) => removeAtPath(current, path));
  };

  const selectScenario = (scenario) => {
    setSelectedId(scenario.scenarioId);
    setScenarioName(scenario.name);
    setDefinition(scenario.definition);
    setResult(null);
    setError('');
  };

  const newScenario = () => {
    setSelectedId(null);
    setScenarioName('Nowy scenariusz');
    setDefinition(defaultDefinition(teams));
    setResult(null);
    setError('');
  };

  const saveScenario = async (asNew = false) => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        competitionId: Number(competitionId),
        name: scenarioName,
        definition,
      };
      const saved = selectedId && !asNew
        ? await updateConditionScenario(selectedId, payload)
        : await createConditionScenario(payload);
      await loadScenarios();
      selectScenario(saved);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeScenario = async (scenarioId) => {
    setSaving(true);
    setError('');
    try {
      await deleteConditionScenario(scenarioId);
      await loadScenarios();
      if (selectedId === scenarioId) newScenario();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    setError('');
    setResult(null);
    try {
      const initialConditions = useInitialConditions
        ? (getValues('initialConditions') || []).map((condition) => ({
            type: condition.type,
            matchId: condition.type === 'specific_match' ? Number(condition.matchId || 0) : 0,
            homeGoals: condition.type === 'specific_match' ? Number(condition.homeGoals || 0) : 0,
            awayGoals: condition.type === 'specific_match' ? Number(condition.awayGoals || 0) : 0,
            teamId: ['all_wins', 'all_losses', 'elo_change', 'points_change'].includes(condition.type) ? Number(condition.teamId || 0) : 0,
            newElo: condition.type === 'elo_change' ? Number(condition.newElo || 0) : 0,
            pointsDelta: condition.type === 'points_change' ? Number(condition.pointsDelta || 0) : 0,
          }))
        : [];
      const data = await analyzeConditionScenario({
        competitionId: Number(competitionId),
        iterations: 10000,
        definition,
        initialConditions,
        excludeScenarioTeamsFromKeyMatches: false,
      });
      setResult(data);
      setResultTab(0);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  if (authLoading || leagueLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  }

  if (!user) {
    return <Alert severity="info">Zaloguj sie, zeby korzystac z Laboratorium scenariuszy.</Alert>;
  }

  if (leagueError) {
    return <Alert severity="error">{leagueError}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 1, md: 2 } }}>
      <Helmet>
        <title>Laboratorium scenariuszy - {leagueTitle} | Symuliga</title>
      </Helmet>

      <Typography variant="h5" component="h1" sx={{ mb: 0.5, fontWeight: 800, textAlign: 'center' }}>
        Laboratorium scenariuszy
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#aaa', textAlign: 'center' }}>
        {leagueTitle}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: '#1e1e1e', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Scenariusze</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={newScenario}>Nowy</Button>
            </Box>
            <Stack spacing={1}>
              {scenarios.map((scenario) => (
                <Paper
                  key={scenario.scenarioId}
                  onClick={() => selectScenario(scenario)}
                  sx={{
                    p: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    border: selectedId === scenario.scenarioId ? '1px solid #1976d2' : '1px solid #333',
                    backgroundColor: selectedId === scenario.scenarioId ? '#203044' : '#252525',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{scenario.name}</Typography>
                    <IconButton size="small" color="error" onClick={(event) => { event.stopPropagation(); removeScenario(scenario.scenarioId); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
              {!scenarios.length && (
                <Typography variant="body2" sx={{ color: '#888' }}>Brak zapisanych scenariuszy.</Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', borderRadius: 1 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Nazwa"
                  value={scenarioName}
                  onChange={(event) => setScenarioName(event.target.value)}
                  sx={{ minWidth: { xs: '100%', md: 320 } }}
                />
              </Box>

              {definition && (
                <ConditionEditor
                  node={definition}
                  path={[]}
                  teams={teams}
                  onChange={handleDefinitionChange}
                  onRemove={handleRemove}
                  root
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={useInitialConditions}
                    onChange={(event) => setUseInitialConditions(event.target.checked)}
                  />
                }
                label="Dodaj warunki początkowe"
              />

              {useInitialConditions && (
                <InitialConditionsForm
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  fields={initialConditionFields}
                  append={appendInitialCondition}
                  remove={removeInitialCondition}
                  matches={matches}
                  teams={teams}
                />
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={analyze} disabled={analyzing || !definition}>
                  {analyzing ? 'Analizuje...' : 'Analizuj'}
                </Button>
                <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => saveScenario(false)} disabled={saving || !definition}>
                  Zapisz
                </Button>
                <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => saveScenario(true)} disabled={saving || !definition}>
                  Zapisz jako nowy
                </Button>
              </Box>
            </Stack>
          </Paper>

          {analyzing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {result && (
            <Paper sx={{ mt: 2, p: 2, backgroundColor: '#1e1e1e', borderRadius: 1 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, backgroundColor: '#252525', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>Prawdopodobieństwo</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#66bb6a' }}>{result.probabilityPercent}%</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Paper sx={{ p: 2, backgroundColor: '#252525', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5 }}>Naturalny opis</Typography>
                    <Typography variant="body1">{result.naturalDescription}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 1 }} />
              <Tabs
                value={resultTab}
                onChange={(_, value) => setResultTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 2,
                  maxWidth: '100%',
                  '& .MuiTabs-scroller': { overflowX: 'auto !important' },
                  '& .MuiTab-root': { minWidth: { xs: 150, sm: 180 } },
                }}
              >
                <Tab label="Przykładowy sezon" />
                <Tab label="Wspierające" />
                <Tab label="Torpedujące" />
              </Tabs>

              {resultTab === 0 && <ExampleSeason example={result.exampleSeason} />}
              {resultTab === 1 && (
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideScenarioTeamMatches}
                        onChange={(event) => setHideScenarioTeamMatches(event.target.checked)}
                      />
                    }
                    label="Ukryj mecze drużyn ujętych w warunkach"
                  />
                  <KeyMatchesTable rows={filterKeyMatches(result.keyMatches?.supporting)} />
                </Stack>
              )}
              {resultTab === 2 && (
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideScenarioTeamMatches}
                        onChange={(event) => setHideScenarioTeamMatches(event.target.checked)}
                      />
                    }
                    label="Ukryj mecze drużyn ujętych w warunkach"
                  />
                  <KeyMatchesTable rows={filterKeyMatches(result.keyMatches?.hindering)} />
                </Stack>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
